import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Building2, CreditCard, Gauge, CheckCircle2, AlertTriangle, Power, Loader2, Users, ShieldX,
  Trash2, Archive, ArchiveRestore, FlaskConical, Unlock, Lock, ShieldCheck, Activity, XCircle, ClipboardCheck,
  FileText, Eye, ClipboardList, KeyRound, ShieldOff, SlidersHorizontal,
} from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { EliminarEntidadDialog } from "@/components/admin/EliminarEntidadDialog";
import { PanelProduccionCFDIEntidad } from "@/components/admin/PanelProduccionCFDIEntidad";
import { PurgaEtapaPruebaPanel } from "@/components/admin/PurgaEtapaPruebaPanel";
import { TooltipButton } from "@/components/common/Tooltips";
import { api } from "@/lib/api";
import { mxn, fdate, fdatetime } from "@/lib/format";
import { formatDomicilio } from "@/lib/domicilio";
import { safeText } from "@/lib/safeText";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SEM = {
  Verde: { label: "Al día", cls: "bg-emerald-100 text-emerald-700" },
  Ambar: { label: "Pendientes", cls: "bg-amber-100 text-amber-700" },
  Rojo: { label: "Crítico", cls: "bg-red-100 text-red-700" },
};

function SemBadge({ semaforo, vencidas }) {
  const m = SEM[semaforo] || SEM.Verde;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {vencidas > 0 ? `${vencidas} vencido${vencidas > 1 ? "s" : ""}` : "Al día"}
    </span>
  );
}

const API_MODO = {
  mock: ["Demo", "bg-slate-100 text-slate-600"],
  sandbox: ["Sandbox", "bg-amber-100 text-amber-700"],
  produccion: ["Producción", "bg-emerald-100 text-emerald-700"],
};

const ACCESO_PAGO = {
  ok: { label: "Acceso al día", cls: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  gracia: { label: "En periodo de gracia", cls: "bg-amber-100 text-amber-700", icon: AlertTriangle },
  restringida: { label: "Acceso restringido por impago", cls: "bg-red-100 text-red-700", icon: Lock },
};

const Card = ({ title, icon: Icon, color = "text-primary", children, testid }) => (
  <section data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
    <h4 className="mb-3 flex items-center gap-1.5 font-heading text-sm font-semibold text-slate-700">
      <Icon className={`h-4 w-4 ${color}`} /> {title}
    </h4>
    {children}
  </section>
);

export default function EntidadDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, meta } = useAuth();
  const [data, setData] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  // Documentos corporativos (feb 2026): CSF y otros documentos de la entidad. Puede venir
  // del wizard de registro (origen='registro') o de /onboarding (origen='onboarding').
  const [docsCorp, setDocsCorp] = useState([]);
  const [docBusy, setDocBusy] = useState(null); // doc_id en descarga
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [delOpen, setDelOpen] = useState(false);
  const [acceso, setAcceso] = useState(null); // { accion: 'reactivar' | 'suspender' }
  const [motivo, setMotivo] = useState("");
  // oct-2026 · Consumo del mes de la entidad: altas (cupo efectivo) y consultas externas de
  // listas. El cupo separado de consultas y su wallet SE ELIMINARON.
  const [qeqUso, setQeqUso] = useState(null);
  // Límite TOTAL de productos de crédito (oct-2026): el super_admin captura el total, el
  // backend calcula el extra sobre los incluidos por el plan. Motivo obligatorio.
  const [productosUso, setProductosUso] = useState(null);
  const [prodAjusteOpen, setProdAjusteOpen] = useState(false);
  const [prodLimiteTotal, setProdLimiteTotal] = useState("");
  const [prodMotivo, setProdMotivo] = useState("");
  const [prodBusy, setProdBusy] = useState(false);
  // Alta de la entidad: información capturada en el wizard de registro + documentos corporativos
  // (Constancia de Situación Fiscal, etc). Se lee del endpoint existente /admin/solicitudes/{id}
  // sin crear nada nuevo en backend. Se muestra en cualquier estatus como respaldo de auditoría.
  const [alta, setAlta] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const roleLabel = (r) => (meta?.roles || []).find((x) => x.id === r)?.label || r;

  // 2FA: acción MFA (activar/desactivar/resetear) sobre un usuario con rol de segundo factor.
  // ago-2026 — el gate del login YA está conectado, así que activar aquí tiene efecto real
  // en el siguiente inicio de sesión del usuario. Aplica a admin_entidad y a
  // oficial_cumplimiento (los roles de `MFA_ROLES` del backend).
  const MFA_ROLES_UI = ["admin_entidad", "oficial_cumplimiento"];
  const [mfaAccion, setMfaAccion] = useState(null); // { user, tipo: 'activar' | 'desactivar' | 'resetear' }
  const [mfaBusy, setMfaBusy] = useState(false);

  const ejecutarMfa = async () => {
    if (!mfaAccion?.user || !mfaAccion?.tipo) return;
    const map = {
      activar: { path: "activate", ok: "2FA activado. El usuario pedirá código por correo desde su siguiente inicio de sesión." },
      desactivar: { path: "deactivate", ok: "MFA desactivado." },
      resetear: { path: "reset", ok: "MFA reseteado. El usuario vuelve a entrar con solo correo y contraseña." },
    };
    setMfaBusy(true);
    try {
      await api.post(`/admin/users/${mfaAccion.user.id}/mfa/${map[mfaAccion.tipo].path}`);
      toast.success(map[mfaAccion.tipo].ok);
      setMfaAccion(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar la acción MFA");
    } finally {
      setMfaBusy(false);
    }
  };

  // Descarga de documento corporativo (feb 2026): pide una URL firmada al backend y abre
  // el archivo en una pestaña nueva. El backend valida que el super_admin sí puede acceder.
  const descargarDoc = async (doc) => {
    setDocBusy(doc.id);
    try {
      const { data } = await api.post(`/onboarding/documentos/${doc.id}/signed-url`);
      const url = data?.url ? `${process.env.REACT_APP_BACKEND_URL}${data.url}` : null;
      if (!url) throw new Error("URL vacía");
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir el documento");
    } finally {
      setDocBusy(null);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: resumen }, { data: hist }, { data: us }, { data: qeq }, { data: prod }, altaRes, docsRes] = await Promise.all([
        api.get(`/admin/entidades/${id}/resumen`),
        api.get(`/admin/entidades/${id}/pagos`),
        api.get(`/admin/entidades/${id}/usuarios`),
        api.get(`/admin/entidades/${id}/qeq-uso`).catch(() => ({ data: null })),
        api.get(`/admin/entidades/${id}/productos-uso`).catch(() => ({ data: null })),
        api.get(`/admin/solicitudes/${id}`).catch(() => null),
        api.get(`/admin/entidades/${id}/documentos-corporativos`).catch(() => ({ data: [] })),
      ]);
      setData(resumen);
      setPagos(hist);
      setUsuarios(us);
      setDocsCorp(docsRes?.data || []);
      setQeqUso(qeq);
      setProductosUso(prod);
      setAlta(altaRes?.data || null);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Abre un documento corporativo (CSF, etc) usando el signed-url del backend.
  // Mismo patrón que Solicitudes.jsx: POST /onboarding/documentos/{doc_id}/signed-url → open_new_tab.
  const verDocCorporativo = async (docId) => {
    try {
      const { data } = await api.post(`/onboarding/documentos/${docId}/signed-url`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir el documento");
    }
  };

  const submitAjusteProductos = async () => {
    const n = Number(prodLimiteTotal);
    if (!Number.isFinite(n) || n < 0) {
      toast.error("Captura el límite total de productos de crédito (un número).");
      return;
    }
    if (prodMotivo.trim().length < 10) {
      toast.error("Escribe el motivo del ajuste (mínimo 10 caracteres): queda en la bitácora.");
      return;
    }
    setProdBusy(true);
    try {
      const { data: r } = await api.post(`/admin/entidades/${id}/ajustar-limite-productos`, {
        limite_total: Math.floor(n), motivo: prodMotivo.trim(),
      });
      toast.success(
        `Límite total de productos: ${r?.limite_total_previo} → ${r?.limite_total} ` +
        `(plan ${r?.limite_plan} + extra ${r?.productos_extra_nuevo}). Queda en bitácora.`
      );
      setProdAjusteOpen(false);
      setProdLimiteTotal(""); setProdMotivo("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo ajustar el límite.");
    } finally {
      setProdBusy(false);
    }
  };

  useEffect(() => {
    if (user && user.role !== "super_admin") { setError(true); setLoading(false); return; }
    if (user) load();
    /* eslint-disable-next-line */
  }, [id, user]);

  const doCambiarEstatus = async () => {
    const nuevo = confirm?.nuevo;
    setConfirm(null);
    if (!nuevo) return;
    setBusy(true);
    try {
      await api.post(`/admin/entidades/${id}/estatus`, { estatus: nuevo });
      toast.success(`Entidad ${nuevo === "Activa" ? "reactivada" : "suspendida"}`);
      await load();
    } catch (e) { toast.error("No se pudo actualizar el estatus"); }
    finally { setBusy(false); }
  };

  const doToggleEntorno = async () => {
    const nuevo = ident.entorno === "demo" ? "produccion" : "demo";
    setBusy(true);
    try {
      await api.put(`/admin/entidades/${id}/entorno`, { entorno: nuevo });
      toast.success(`Entorno cambiado a ${nuevo === "demo" ? "Demo / prueba" : "Producción"}`);
      await load();
    } catch (e) { toast.error("No se pudo cambiar el entorno"); }
    finally { setBusy(false); }
  };

  const doDesarchivar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/entidades/${id}/desarchivar`, {});
      toast.success(`Entidad restaurada. ${data.usuarios_reactivados} usuario(s) reactivado(s).`);
      await load();
    } catch (e) { toast.error("No se pudo desarchivar"); }
    finally { setBusy(false); }
  };

  // Autorizar entidad (pendiente_aprobacion → Activa). Solo visible en ese estatus.
  const [rechazoOpen, setRechazoOpen] = useState(false);
  const [rechazoMotivo, setRechazoMotivo] = useState("");

  const doAutorizar = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/solicitudes/${id}/aprobar`);
      toast.success("Entidad autorizada. El acceso operativo quedó habilitado y se notificó al representante.");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo autorizar la entidad"); }
    finally { setBusy(false); }
  };

  const doRechazarSolicitud = async () => {
    if (!rechazoMotivo.trim()) { toast.error("Indica el motivo del rechazo"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/solicitudes/${id}/rechazar`, { motivo: rechazoMotivo, definitivo: false });
      toast.success("Solicitud rechazada. El representante podrá corregir y reenviar.");
      setRechazoOpen(false); setRechazoMotivo("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo rechazar la solicitud"); }
    finally { setBusy(false); }
  };

  const doAcceso = async () => {
    const accion = acceso?.accion;
    const m = motivo.trim();
    if (!accion || !m) { toast.error("El motivo es obligatorio."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/entidades/${id}/acceso/${accion}`, { motivo: m });
      toast.success(accion === "reactivar"
        ? "Acceso reactivado manualmente. La restricción por impago queda anulada."
        : "Acceso suspendido manualmente. La entidad queda confinada a Facturación.");
      setAcceso(null);
      setMotivo("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar el override de acceso");
    } finally { setBusy(false); }
  };

  if (error)
    return (
      <div>
        <PageHeader title="Ficha de entidad" subtitle="Panel de plataforma" />
        <div className="flex flex-col items-center justify-center gap-3 p-16 text-slate-400">
          <ShieldX className="h-10 w-10" />
          <p className="text-sm">Acceso exclusivo del Super Administrador de la plataforma.</p>
          <Button data-testid="entidad-volver-error" variant="outline" onClick={() => navigate("/admin-saas")}>Volver</Button>
        </div>
      </div>
    );

  const ident = data?.identidad || {};
  const s = data?.suscripcion || {};
  const uso = data?.uso || {};
  const cmp = data?.cumplimiento || {};
  const salud = data?.salud || {};
  const acc = data?.acceso || {};
  const accInfo = ACCESO_PAGO[acc.acceso_pago] || ACCESO_PAGO.ok;
  const suspendida = ident.estatus !== "Activa";
  const archivada = ident.estatus === "archivada";

  return (
    <div data-testid="entidad-detalle">
      <PageHeader
        title={ident.nombre_comercial || "Ficha de entidad"}
        subtitle="Suscripción, uso, cumplimiento y cuentas administrativas (sin datos operativos)"
      >
        <Button data-testid="entidad-volver-btn" variant="outline" size="sm" onClick={() => navigate("/admin-saas")}>
          <ArrowLeft className="mr-1.5 h-4 w-4" /> Volver a Admin SaaS
        </Button>
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : data ? (
        <div className="space-y-6 p-6 md:p-8">
          {/* Identidad */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <Building2 className="h-5 w-5 text-primary" />
              </span>
              <div className="text-sm">
                <p className="font-heading text-base font-semibold text-slate-900">{ident.razon_social}</p>
                <p className="text-xs text-slate-500">RFC {safeText(ident.rfc)} · {safeText(ident.regimen)} · Alta {fdate(ident.fecha_alta)}</p>
              </div>
            </div>
            <StatusBadge value={ident.estatus} />
          </div>

          {/* Banner de autorización pendiente (solo si estatus === pendiente_aprobacion) */}
          {ident.estatus === "pendiente_aprobacion" && (
            <div data-testid="entidad-banner-autorizar"
              className="flex flex-wrap items-start gap-4 rounded-lg border-2 border-amber-300 bg-amber-50 px-5 py-4 shadow-sm">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-100">
                <ClipboardCheck className="h-5 w-5 text-amber-700" />
              </span>
              <div className="min-w-[260px] flex-1">
                <p className="font-heading text-sm font-semibold text-amber-900">Esta entidad está pendiente de tu autorización</p>
                <p className="mt-1 text-xs text-amber-800">
                  El pago fue confirmado y la documentación corporativa está cargada. Al autorizar, se habilitará el acceso
                  operativo de la entidad y se notificará por correo al representante legal.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <TooltipButton
                  tooltip="Habilita el acceso operativo de esta entidad y envía correo al representante"
                  data-testid="entidad-autorizar-btn"
                  disabled={busy}
                  onClick={doAutorizar}
                  className="bg-emerald-600 text-white hover:bg-emerald-700">
                  {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                  Autorizar entidad
                </TooltipButton>
                <TooltipButton
                  tooltip="Rechaza la solicitud con motivo (el representante podrá corregir y reenviar)"
                  data-testid="entidad-rechazar-btn" variant="outline"
                  disabled={busy}
                  onClick={() => setRechazoOpen(true)}
                  className="border-red-200 text-red-600 hover:bg-red-50">
                  <XCircle className="mr-1.5 h-4 w-4" />
                  Rechazar solicitud
                </TooltipButton>
              </div>
            </div>
          )}

          {/* Información capturada en el alta (registro/onboarding).
              Se muestra en cualquier estatus (auditoría permanente). Fuente: /admin/solicitudes/{id}. */}
          {alta && (
            <Card title="Información del alta (registro de la entidad)" icon={ClipboardList} color="text-primary" testid="entidad-info-alta">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {(() => {
                  const oc = alta.oficial_cumplimiento;
                  const ocResumen = (oc && (oc.nombre || oc.correo || oc.email || oc.telefono))
                    ? [oc.nombre, (oc.correo || oc.email), oc.telefono].filter(Boolean).join(" · ")
                    : "No declarado";
                  const tipoOpsResumen = Array.isArray(alta.tipo_operaciones) && alta.tipo_operaciones.length
                    ? alta.tipo_operaciones.join(", ")
                    : "—";
                  return [
                    ["Nombre comercial", alta.nombre_comercial],
                    ["Razón social", alta.razon_social],
                    ["RFC", alta.rfc],
                    ["Régimen", alta.regimen],
                    ["Tipo de entidad", alta.tipo_entidad],
                    ["Supervisor", alta.supervisor],
                    ["Tipo de operaciones", tipoOpsResumen],
                    ["Oficial de Cumplimiento", ocResumen],
                    ["Domicilio fiscal", formatDomicilio(alta.domicilio)],
                    ["Teléfono de contacto", alta.telefono],
                    ["Correo de contacto", alta.correo],
                    ["Clave SIPRES", alta.regulatorio?.clave_sipres],
                    ["Representante legal", alta.representante],
                    ["Correo del representante", alta.representante_correo],
                    ["Fecha de alta", alta.fecha_alta ? fdate(alta.fecha_alta) : null],
                  ];
                })().map(([l, v]) => (
                  <div key={l} className="rounded-md border border-slate-100 bg-slate-50/60 p-2.5" data-testid={`alta-campo-${l.replace(/\s+/g, "-").toLowerCase()}`}>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-0.5 truncate text-sm font-medium text-slate-900">{v || "—"}</p>
                  </div>
                ))}
              </div>
              {alta.motivo_rechazo && (
                <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="alta-motivo-rechazo">
                  <span className="font-semibold">Motivo del último rechazo:</span> {alta.motivo_rechazo}
                </div>
              )}
            </Card>
          )}

          {/* Documentos corporativos del alta (CSF, etc). Fuente: /admin/solicitudes/{id}.
              Se muestra en cualquier estatus como respaldo de auditoría. */}
          {alta && Array.isArray(alta.documentos) && alta.documentos.length > 0 && (
            <Card title="Documentos corporativos del alta" icon={FileText} color="text-primary" testid="entidad-docs-corporativos">
              <p className="mb-3 text-xs text-slate-500">
                Archivos que la entidad cargó en el registro. Se conservan como respaldo de auditoría.
              </p>
              <div className="space-y-2">
                {alta.documentos.map((doc) => (
                  <div key={doc.tipo}
                    data-testid={`doc-corp-${doc.tipo}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white px-3 py-2.5">
                    <div className="flex min-w-0 items-center gap-2.5">
                      {doc.subido ? (
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                      ) : (
                        <FileText className="h-4 w-4 shrink-0 text-slate-300" />
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-800">{doc.label}</p>
                        <p className="truncate text-xs text-slate-500">
                          {doc.subido ? `${doc.nombre_archivo || "archivo"} · ${doc.fecha ? fdate(doc.fecha) : "sin fecha"}` : "No cargado"}
                        </p>
                      </div>
                    </div>
                    {doc.subido && doc.doc_id && (
                      <Button data-testid={`doc-corp-ver-${doc.tipo}`} variant="ghost" size="sm"
                        onClick={() => verDocCorporativo(doc.doc_id)}>
                        <Eye className="mr-1 h-4 w-4" /> Ver
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Suscripción y pagos */}
          <Card title="Suscripción y pagos" icon={CreditCard} color="text-forest-600" testid="entidad-suscripcion">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {[
                ["Plan", s.plan],
                ["Monto", mxn(s.monto)],
                ["Estado de pago", <span key="estado-pago-val" data-testid="entidad-estatus-pago" className={`font-semibold ${s.estatus_pago === "Al corriente" ? "text-emerald-600" : "text-red-600"}`}>{s.estatus_pago}</span>],
                ["Periodicidad", s.periodicidad],
                ["Último pago", fdate(s.ultimo_pago_exitoso)],
                ["Próximo cobro", fdate(s.proximo_cobro)],
                ["Método", s.metodo_pago_last4 ? `•••• ${s.metodo_pago_last4}` : (s.metodo_pago || "—")],
                ["Intentos fallidos", s.intentos_cobro ?? 0],
              ].map(([l, v]) => (
                <div key={l} className="rounded-md border border-slate-100 bg-slate-50/60 p-2.5">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">{l}</p>
                  <p className="mt-0.5 text-sm font-medium text-slate-900">{v ?? "—"}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Acceso por pago — override manual del super_admin */}
          <Card title="Acceso por pago (override manual)" icon={ShieldCheck} color="text-forest-600" testid="entidad-acceso-pago">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Estado del acceso</p>
                <span data-testid="acceso-pago-badge" className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${accInfo.cls}`}>
                  <accInfo.icon className="h-3.5 w-3.5" /> {accInfo.label}
                </span>
                {acc.pago_gracia_limite && acc.acceso_pago === "gracia" && (
                  <p className="mt-1.5 text-xs text-slate-500">Gracia hasta {fdate(acc.pago_gracia_limite)}.</p>
                )}
                <p className="mt-2 max-w-xl text-xs text-slate-500">
                  El override manual permite otorgar una prórroga (reactivar) o bloquear de inmediato (suspender) el
                  acceso de la entidad por motivos de pago, con independencia de Stripe. El motivo queda en bitácora.
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                <TooltipButton tooltip="Reactivar el acceso"
                  data-testid="acceso-reactivar-btn" variant="default" size="sm" disabled={busy}
                  className="bg-accent hover:bg-forest-700"
                  onClick={() => { setMotivo(""); setAcceso({ accion: "reactivar" }); }}>
                  <Unlock className="mr-1.5 h-4 w-4" /> Reactivar acceso
                </TooltipButton>
                <TooltipButton tooltip="Suspender el acceso pagado (por impago o riesgo)"
                  data-testid="acceso-suspender-btn" variant="outline" size="sm" disabled={busy}
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  onClick={() => { setMotivo(""); setAcceso({ accion: "suspender" }); }}>
                  <Lock className="mr-1.5 h-4 w-4" /> Suspender acceso
                </TooltipButton>
              </div>
            </div>
            {acc.override_manual && (
              <div data-testid="acceso-override-activo" className="mt-4 rounded-md border border-forest-200 bg-forest-50/60 px-3 py-2.5 text-xs text-slate-600">
                <p className="flex items-center gap-1.5 font-semibold text-forest-700">
                  <ShieldCheck className="h-3.5 w-3.5" /> Override manual activo
                  {acc.override_accion ? ` · ${acc.override_accion === "reactivar" ? "Reactivación" : "Suspensión"}` : ""}
                </p>
                {acc.override_motivo && <p className="mt-1">Motivo: <span className="font-medium text-slate-700">{acc.override_motivo}</span></p>}
                <p className="mt-0.5 text-slate-500">
                  Por {acc.override_por || "—"}{acc.override_en ? ` · ${fdatetime(acc.override_en)}` : ""}
                </p>
              </div>
            )}
          </Card>
          <Card title="Uso de la cuenta" icon={Gauge} color="text-blue-600" testid="entidad-uso">
            <p className="text-sm text-slate-600"><b className="tabular-nums">{uso.clientes_activos}</b> clientes activos · clientes y usuarios ilimitados en el plan.</p>
          </Card>

          {/* Cumplimiento agregado */}
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-sm" data-testid="entidad-cumplimiento">
            <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
              {cmp.semaforo === "Verde" ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
              Cumplimiento regulatorio (agregado)
            </span>
            <SemBadge semaforo={cmp.semaforo} vencidas={cmp.obligaciones_vencidas} />
          </div>

          {/* Salud de operación */}
          <Card title="Salud de la operación (agregada)" icon={Gauge} color="text-forest-600" testid="entidad-salud">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3 text-center">
                <p className="text-lg font-semibold text-slate-900">{salud.creditos_activos ?? 0}</p>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Créditos activos</p>
              </div>
              <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3 text-center">
                <p className="text-lg font-semibold text-amber-600">{salud.reportes_proximos ?? 0}</p>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Reportes por vencer</p>
                {salud.reporte_proximo_fecha && <p className="mt-0.5 text-[10px] text-slate-400">{fdate(salud.reporte_proximo_fecha)}</p>}
              </div>
              <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3 text-center">
                <p className={`text-lg font-semibold ${(salud.reportes_vencidos ?? 0) > 0 ? "text-red-600" : "text-emerald-600"}`}>{salud.reportes_vencidos ?? 0}</p>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Reportes vencidos</p>
              </div>
              <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3 text-center">
                <p className={`text-lg font-semibold ${(salud.dias_para_vencer ?? 99) <= 7 ? "text-red-600" : "text-slate-900"}`}>{salud.dias_para_vencer ?? "—"}</p>
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Días para vencer paquete</p>
                {salud.suscripcion_vence && <p className="mt-0.5 text-[10px] text-slate-400">{fdate(salud.suscripcion_vence)}</p>}
              </div>
            </div>
            <div className="mt-4">
              <p className="mb-1.5 text-[11px] uppercase tracking-wide text-slate-500">Conexión de API por proveedor</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(salud.api_modos || {}).map(([prov, modo]) => {
                  const [lbl, cls] = API_MODO[modo] || API_MODO.mock;
                  const provLabel = { condusef: "CONDUSEF", buro_pf: "Buró PF", buro_pm: "Buró PM", circulo: "Círculo" }[prov] || prov;
                  return (
                    <span key={prov} className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${cls}`}>
                      {provLabel}: {lbl}
                    </span>
                  );
                })}
              </div>
            </div>
          </Card>

          {/* Documentos corporativos (feb 2026): lista EXHAUSTIVA de todos los docs de la
              entidad, cualquiera sea su origen (registro o onboarding). Complementa la Card
              anterior "Documentos corporativos del alta" (checklist) con la lista real. */}
          <Card title={`Documentos corporativos — completos (${docsCorp.length})`} icon={FileText}
                color="text-primary" testid="entidad-docs-completos">
            {docsCorp.length === 0 ? (
              <p className="text-sm text-slate-500" data-testid="entidad-docs-empty">
                Esta entidad aún no ha subido documentos corporativos (Constancia de Situación Fiscal, etc.).
              </p>
            ) : (
              <DataTable
                testid="entidad-docs-tabla"
                rows={docsCorp.map((d) => ({
                  ...d,
                  origen_label: d.origen === "registro" ? "Registro" : "Onboarding",
                  fecha_carga_fmt: d.fecha_carga ? fdatetime(d.fecha_carga) : "—",
                  tamano_fmt: d.tamano ? `${(d.tamano / 1024).toFixed(1)} KB` : "—",
                }))}
                columns={[
                  { key: "label", label: "Documento" },
                  { key: "nombre_archivo", label: "Archivo" },
                  { key: "tamano_fmt", label: "Peso" },
                  { key: "fecha_carga_fmt", label: "Fecha de carga" },
                  { key: "subido_por", label: "Subido por" },
                  { key: "origen_label", label: "Origen", kind: "badge" },
                ]}
                actions={
                  // eslint-disable-next-line react/no-unstable-nested-components
                  (row) => (
                  <TooltipButton tooltip="Descargar / previsualizar el documento en una pestaña nueva"
                    data-testid={`entidad-doc-descargar-${row.id}`} variant="ghost" size="sm"
                    className="h-8 gap-1.5 px-2.5"
                    onClick={() => descargarDoc(row)}
                    disabled={docBusy === row.id}>
                    {docBusy === row.id
                      ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                      : <Eye className="h-4 w-4 text-primary" />} Ver / descargar
                  </TooltipButton>
                )}
                emptyLabel="Sin documentos"
              />
            )}
            <p className="mt-2 text-[11px] text-slate-500">
              Fuentes: <b>Registro</b> = subido durante el alta de la entidad (feb 2026 en adelante) ·{" "}
              <b>Onboarding</b> = subido desde la pantalla de onboarding (flujo anterior).
            </p>
          </Card>

          {/* Usuarios (cuentas administrativas, sin PII operativa) */}
          <Card title={`Usuarios de la cuenta (${usuarios.length})`} icon={Users} color="text-primary" testid="entidad-usuarios">
            <DataTable
              testid="entidad-usuarios-tabla"
              rows={usuarios.map((u) => ({
                ...u,
                role_label: roleLabel(u.role),
                estatus: u.activo ? "Activo" : "Inactivo",
                ultimo_acceso_fmt: u.ultimo_acceso ? fdatetime(u.ultimo_acceso) : "Nunca",
              }))}
              columns={[
                { key: "name", label: "Nombre" },
                { key: "email", label: "Correo" },
                { key: "role_label", label: "Rol", kind: "badge" },
                { key: "estatus", label: "Estatus", kind: "badge" },
                { key: "ultimo_acceso_fmt", label: "Último acceso" },
                {
                  key: "mfa", label: "MFA (2FA)",
                  render: (row) => {
                    // Solo los roles con segundo factor muestran estado; el resto "—".
                    if (!MFA_ROLES_UI.includes(row.role)) {
                      return <span className="text-slate-400" data-testid={`mfa-badge-${row.id}`}>—</span>;
                    }
                    const on = !!row.mfa_activo;
                    return (
                      <span data-testid={`mfa-badge-${row.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          on ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        <ShieldCheck className="h-3 w-3" />
                        {on ? "Activo" : "Inactivo"}
                      </span>
                    );
                  }
                },
              ]}
              actions={
                // eslint-disable-next-line react/no-unstable-nested-components
                (row) => {
                if (!MFA_ROLES_UI.includes(row.role)) return null;
                const on = !!row.mfa_activo;
                return (
                  <div className="flex items-center justify-end gap-1">
                    {on ? (
                      <TooltipButton tooltip="Desactivar el 2FA por correo para este usuario"
                        data-testid={`mfa-toggle-${row.id}`} variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5"
                        onClick={() => setMfaAccion({ user: row, tipo: "desactivar" })}>
                        <ShieldOff className="h-4 w-4 text-slate-500" /> Desactivar MFA
                      </TooltipButton>
                    ) : (
                      <TooltipButton tooltip="Activar el 2FA por correo para este usuario (efectivo en su siguiente inicio de sesión)"
                        data-testid={`mfa-toggle-${row.id}`} variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5"
                        onClick={() => setMfaAccion({ user: row, tipo: "activar" })}>
                        <ShieldCheck className="h-4 w-4 text-emerald-600" /> Activar MFA
                      </TooltipButton>
                    )}
                    <TooltipButton tooltip="Rescate: apaga el MFA e invalida códigos vigentes (paracaídas de emergencia)"
                      data-testid={`mfa-reset-${row.id}`} variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5"
                      onClick={() => setMfaAccion({ user: row, tipo: "resetear" })}>
                      <KeyRound className="h-4 w-4 text-amber-600" /> Resetear MFA
                    </TooltipButton>
                  </div>
                );
              }}
              emptyLabel="Sin usuarios registrados"
            />
            <p className="mt-2 text-[11px] text-slate-500">
              El 2FA por correo aplica a los roles Administrador de la entidad y Oficial de Cumplimiento.
              Los usuarios nuevos de esos roles nacen con el 2FA <b>activo</b>: su primer inicio de sesión
              entra sin código y desde el segundo siempre se pide. Usa &quot;Resetear MFA&quot; si un usuario
              se queda fuera porque no le llega el correo.
            </p>
          </Card>

          {/* Acciones SaaS */}
          <div className="flex flex-wrap gap-2">
            <TooltipButton tooltip={suspendida ? "Reactivar el acceso pagado (levanta la suspensión por impago)" : "Suspender el acceso pagado (por impago o riesgo)"}
              data-testid="toggle-estatus-btn" variant={suspendida ? "default" : "outline"} disabled={busy}
              className={suspendida ? "bg-accent hover:bg-forest-700" : "border-red-200 text-red-600 hover:bg-red-50"}
              onClick={() => setConfirm({ nuevo: suspendida ? "Activa" : "Suspendida" })}>
              <Power className="mr-1.5 h-4 w-4" /> {suspendida ? "Reactivar entidad" : "Suspender por pago"}
            </TooltipButton>
          </div>

          {/* oct-2026 · Consumo del mes. El cupo separado de consultas externas se ELIMINÓ:
              su techo es el MISMO cupo efectivo de altas del mes de la entidad, así que aquí
              solo se informa el consumo real (dato de costo) junto a las altas. */}
          {qeqUso?.estado && (
            <Card title="Altas y consultas externas del mes" icon={Activity} color="text-primary"
              testid="entidad-consumo-mes">
              {qeqUso.estado.grandfathered ? (
                <div className="text-sm text-slate-500" data-testid="consumo-grandfathered">
                  Entidad legacy sin candado de plan (grandfathered): sus altas y sus consultas
                  externas de listas no se limitan ni se descuentan.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
                      <div className="text-xs text-slate-500">Altas del mes</div>
                      <div className="mt-0.5 text-lg font-semibold text-slate-800"
                        data-testid="consumo-altas-mes">
                        {qeqUso.estado.unlimited
                          ? `${(qeqUso.estado.altas_usadas_mes ?? 0).toLocaleString("es-MX")} · ilimitadas`
                          : `${(qeqUso.estado.altas_usadas_mes ?? 0).toLocaleString("es-MX")} de ${(qeqUso.estado.cupo_altas_mes ?? 0).toLocaleString("es-MX")}`}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        Cupo efectivo del mes (plan + ajuste del super administrador)
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
                      <div className="text-xs text-slate-500">Consultas externas consumidas este mes</div>
                      <div className="mt-0.5 text-lg font-semibold text-slate-800"
                        data-testid="consumo-consultas-externas">
                        {(qeqUso.estado.consultas_consumidas_mes ?? 0).toLocaleString("es-MX")}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        Costo real de la plataforma · periodo {qeqUso.estado.periodo}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
                      <div className="text-xs text-slate-500">Semáforo</div>
                      <div className={`mt-0.5 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${
                        qeqUso.estado.semaforo === "rojo" ? "bg-red-100 text-red-700"
                        : qeqUso.estado.semaforo === "ambar" ? "bg-amber-100 text-amber-700"
                        : "bg-emerald-100 text-emerald-700"
                      }`} data-testid="qeq-semaforo-entidad">
                        {qeqUso.estado.semaforo === "rojo" ? "Cupo del mes agotado"
                          : qeqUso.estado.semaforo === "ambar" ? "Al 80% del cupo" : "Al día"}
                      </div>
                    </div>
                  </div>
                  <p className="mt-4 border-t border-slate-100 pt-4 text-xs text-slate-500">
                    Una consulta externa de listas ocurre al dar de alta a una persona: su techo es
                    el <b>mismo cupo de altas del mes</b>. Para ampliarlo usa «Ajustar altas» en la
                    lista de entidades; ya no existe una bolsa de consultas aparte.
                  </p>
                </>
              )}
            </Card>
          )}

          {/* Candado de productos por plan (jul-2026, iter_80). Card que muestra el uso y
              permite al super_admin ampliar el cupo cuando el cliente paga por fuera. */}
          {productosUso && (
            <Card title="Productos de crédito por plan" icon={ClipboardList} color="text-primary"
              testid="productos-uso-card">
              {productosUso.enforcement === false ? (
                <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600"
                  data-testid="productos-grandfathered">
                  Entidad legacy sin candado de productos (grandfathered). Activa el
                  enforcement del plan si necesitas aplicar el límite.
                </div>
              ) : productosUso.unlimited ? (
                <div className="text-sm text-slate-700" data-testid="productos-ilimitado">
                  Plan <b>{productosUso.plan_id}</b> · Productos ilimitados. Activos actuales:
                  <b> {productosUso.activos}</b>.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
                      <div className="text-xs text-slate-500">Productos activos</div>
                      <div className="mt-0.5 text-lg font-semibold text-slate-800"
                        data-testid="productos-activos">
                        {productosUso.activos.toLocaleString("es-MX")}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
                      <div className="text-xs text-slate-500">Incluidos por plan</div>
                      <div className="mt-0.5 text-lg font-semibold text-slate-800">
                        {productosUso.limite_plan.toLocaleString("es-MX")}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
                      <div className="text-xs text-slate-500">Límite total efectivo</div>
                      <div className="mt-0.5 text-lg font-semibold text-slate-800"
                        data-testid="productos-limite-total">
                        {productosUso.limite_efectivo.toLocaleString("es-MX")}
                      </div>
                    </div>
                    <div className="rounded-md border border-slate-100 bg-slate-50/60 p-3">
                      <div className="text-xs text-slate-500">Cupo restante</div>
                      <div className={
                        "mt-0.5 text-lg font-semibold " +
                        (productosUso.disponibles === 0
                          ? "text-red-600"
                          : productosUso.disponibles <= 2
                          ? "text-amber-600"
                          : "text-emerald-700")
                      } data-testid="productos-disponibles">
                        {productosUso.disponibles.toLocaleString("es-MX")}
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-4">
                    <p className="text-xs text-slate-500">
                      El límite total efectivo es {productosUso.limite_efectivo}: {productosUso.limite_plan}{" "}
                      incluidos por el plan + {productosUso.productos_extra} extra calculados por el
                      ajuste. Es el mismo número que ve la entidad en Facturación. Los productos
                      existentes no se afectan al bajar el plan.
                    </p>
                    <Button data-testid="btn-abrir-ajuste-productos"
                      onClick={() => {
                        setProdLimiteTotal(String(productosUso.limite_efectivo ?? ""));
                        setProdAjusteOpen(true);
                      }} disabled={busy}
                      className="bg-accent hover:bg-forest-700">
                      <SlidersHorizontal className="mr-1.5 h-4 w-4" /> Ajustar límite de productos
                    </Button>
                  </div>
                  {(productosUso.recargas_recientes || []).length > 0 && (
                    <div className="mt-4 border-t border-slate-100 pt-4">
                      <p className="mb-2 text-xs font-medium text-slate-600">Últimos ajustes</p>
                      <div className="max-h-40 space-y-1.5 overflow-y-auto">
                        {productosUso.recargas_recientes.slice(0, 5).map((r) => (
                          <div key={r.id}
                            className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
                            <span className="font-mono">{fdatetime(r.creado_at)}</span>
                            <span className="font-semibold text-slate-800">
                              {r.limite_total_nuevo != null
                                ? `límite total ${r.limite_total_previo} → ${r.limite_total_nuevo}`
                                : `+${r.cantidad}`}
                            </span>
                            <span className="text-slate-500">por {r.creado_por_email || r.creado_por_nombre}</span>
                            {(r.motivo || r.nota) && <span className="italic text-slate-500">· {r.motivo || r.nota}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </Card>
          )}

          {/* jun-2026 — Encendido de producción CFDI + Carta Manifiesto (RMF 2026) de ESTA
              entidad. Exclusivo super_admin: el backend responde 403 real a cualquier otro
              rol; la entidad nunca ve estos bloques. */}
          {user?.role === "super_admin" && <PanelProduccionCFDIEntidad entityId={id} />}

          {/* sep-2026 — Depuración de la etapa inicial de la cuenta. Exclusivo super_admin:
              la marca de arranque vive en una colección de plataforma y la entidad no la ve
              en ninguna respuesta suya ni en su bitácora. */}
          {user?.role === "super_admin" && <PurgaEtapaPruebaPanel entityId={id} />}

          {/* Zona de administración de plataforma (entorno + archivar/eliminar) */}
          <Card title="Administración de plataforma" icon={ShieldX} color="text-red-600" testid="entidad-danger-zone">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-sm">
                <p className="flex items-center gap-1.5 font-medium text-slate-700">
                  <FlaskConical className="h-4 w-4 text-slate-400" /> Entorno de la entidad
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  Estado actual:{" "}
                  <span data-testid="entidad-entorno-badge" className={`rounded-full px-2 py-0.5 text-xs font-semibold ${ident.entorno === "demo" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                    {ident.entorno === "demo" ? "Demo / prueba" : "Producción"}
                  </span>
                  {ident.entorno !== "demo" && " · el borrado duro solo se permite en entidades demo."}
                </p>
              </div>
              <TooltipButton tooltip="Marca la entidad como Demo (baja consecuencia) o Producción"
                data-testid="toggle-entorno-btn" variant="outline" size="sm" disabled={busy} onClick={doToggleEntorno}>
                Marcar como {ident.entorno === "demo" ? "Producción" : "Demo"}
              </TooltipButton>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              {archivada ? (
                <TooltipButton tooltip="Desarchivar y volver a activar"
                  data-testid="desarchivar-btn" variant="outline" disabled={busy}
                  className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={doDesarchivar}>
                  <ArchiveRestore className="mr-1.5 h-4 w-4" /> Desarchivar (restaurar acceso)
                </TooltipButton>
              ) : (
                <TooltipButton tooltip="Archivar (desaparece del listado) o eliminar definitivamente (irreversible)"
                  data-testid="abrir-eliminar-btn" variant="outline" disabled={busy}
                  className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDelOpen(true)}>
                  <Trash2 className="mr-1.5 h-4 w-4" /> Archivar o eliminar entidad…
                </TooltipButton>
              )}
              {archivada && (
                <span className="inline-flex items-center gap-1.5 rounded-md bg-slate-100 px-3 py-1.5 text-xs text-slate-500">
                  <Archive className="h-3.5 w-3.5" /> Entidad archivada — acceso deshabilitado, datos conservados.
                </span>
              )}
            </div>
          </Card>

          {/* Historial de pagos */}
          <Card title="Historial de pagos" icon={CreditCard} color="text-slate-500" testid="entidad-pagos">
            <DataTable testid="entidad-pagos-tabla" rows={pagos}
              columns={[
                { key: "fecha", label: "Fecha", kind: "date" },
                { key: "monto", label: "Monto", kind: "currency" },
                { key: "metodo", label: "Método" },
                { key: "estatus", label: "Estatus", kind: "badge" },
                { key: "folio", label: "Folio" },
              ]} emptyLabel="Sin pagos registrados" />
          </Card>
        </div>
      ) : null}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent data-testid="confirm-estatus-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              {confirm?.nuevo === "Activa" ? "Reactivar entidad" : "Suspender entidad por pago"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.nuevo === "Activa"
                ? "La entidad recuperará el acceso completo a la plataforma. ¿Confirmas la reactivación?"
                : "Se suspenderá el acceso de la entidad por falta de pago (sin borrar su información). ¿Confirmas la suspensión?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-estatus-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-estatus-ok" onClick={doCambiarEstatus} className="bg-accent hover:bg-forest-700">
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EliminarEntidadDialog
        open={delOpen}
        onOpenChange={setDelOpen}
        entityId={id}
        nombre={ident.nombre_comercial || ident.razon_social}
        onDone={load}
      />

      {/* 2FA Fase 3: confirmación de acción MFA sobre un admin_entidad de esta cuenta */}
      <AlertDialog open={!!mfaAccion} onOpenChange={(v) => !v && setMfaAccion(null)}>
        <AlertDialogContent data-testid="mfa-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading flex items-center gap-2">
              {mfaAccion?.tipo === "resetear"
                ? <><KeyRound className="h-4 w-4 text-amber-600" /> Resetear MFA (paracaídas)</>
                : mfaAccion?.tipo === "activar"
                  ? <><ShieldCheck className="h-4 w-4 text-emerald-600" /> Activar 2FA por correo</>
                  : <><ShieldOff className="h-4 w-4 text-slate-500" /> Desactivar 2FA por correo</>}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              {mfaAccion?.user && (
                <span className="block text-sm text-slate-600">
                  Usuario: <b>{mfaAccion.user.name}</b> <span className="text-slate-400">({mfaAccion.user.email})</span>
                </span>
              )}
              {mfaAccion?.tipo === "resetear" && (
                <span className="block text-sm text-slate-700">
                  Se pondrá <b>mfa_activo = False</b> y se invalidarán los códigos MFA vigentes.
                  El usuario volverá a entrar con solo correo y contraseña.
                  Se registra en bitácora como <code>mfa_reset_super_admin</code>.
                </span>
              )}
              {mfaAccion?.tipo === "activar" && (
                <span className="block text-sm text-slate-700">
                  Se pondrá <b>mfa_activo = True</b> para este usuario. Desde su siguiente inicio
                  de sesión se le pedirá el código de 6 dígitos que recibirá por correo.
                </span>
              )}
              {mfaAccion?.tipo === "desactivar" && (
                <span className="block text-sm text-slate-700">
                  Se pondrá <b>mfa_activo = False</b>. El usuario entrará como hoy, sin código.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mfa-confirm-cancel" disabled={mfaBusy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="mfa-confirm-ok" onClick={ejecutarMfa} disabled={mfaBusy}
              className={mfaAccion?.tipo === "resetear"
                ? "bg-amber-600 hover:bg-amber-700"
                : mfaAccion?.tipo === "activar" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-600 hover:bg-slate-700"}>
              {mfaBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {mfaAccion?.tipo === "resetear" ? "Confirmar reset"
                : mfaAccion?.tipo === "activar" ? "Activar MFA" : "Desactivar MFA"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!acceso} onOpenChange={(v) => { if (!v) { setAcceso(null); setMotivo(""); } }}>
        <DialogContent data-testid="acceso-override-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {acceso?.accion === "reactivar" ? "Reactivar acceso (override manual)" : "Suspender acceso (override manual)"}
            </DialogTitle>
            <DialogDescription>
              {acceso?.accion === "reactivar"
                ? "Otorga acceso a la entidad de forma manual, anulando la restricción por impago (prórroga). No cobra ni toca Stripe."
                : "Restringe de inmediato el acceso de la entidad por motivos de pago. Quedará confinada a la pantalla de Facturación."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="acceso-motivo" className="text-sm font-medium">Motivo (obligatorio)</Label>
            <Textarea
              id="acceso-motivo"
              data-testid="acceso-motivo-input"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={acceso?.accion === "reactivar"
                ? "Ej. Prórroga autorizada por excepción de cobro; pago en proceso de conciliación."
                : "Ej. Falta de pago confirmada tras vencer el periodo de gracia; bloqueo solicitado por finanzas."}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button data-testid="acceso-override-cancel" variant="outline" onClick={() => { setAcceso(null); setMotivo(""); }} disabled={busy}>
              Cancelar
            </Button>
            <Button
              data-testid="acceso-override-confirm"
              onClick={doAcceso}
              disabled={busy || !motivo.trim()}
              className={acceso?.accion === "reactivar" ? "bg-accent hover:bg-forest-700" : "bg-red-600 hover:bg-red-700"}
            >
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
              {acceso?.accion === "reactivar" ? "Reactivar acceso" : "Suspender acceso"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: LÍMITE TOTAL de productos de crédito (oct-2026) */}
      <Dialog open={prodAjusteOpen} onOpenChange={(v) => { if (!v) { setProdAjusteOpen(false); setProdLimiteTotal(""); setProdMotivo(""); } }}>
        <DialogContent data-testid="prod-ajuste-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Ajustar límite de productos</DialogTitle>
            <DialogDescription>
              Captura el <b>límite TOTAL</b> de productos de crédito que puede tener esta
              entidad (no un incremento). El sistema calcula los extra sobre los
              incluidos por su plan. El pago se acuerda fuera del sistema (no toca Stripe).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="prod-limite-total" className="text-sm font-medium">Límite total de productos *</Label>
              <Input id="prod-limite-total" data-testid="prod-limite-total-input" type="number"
                min={productosUso?.limite_plan ?? 0}
                value={prodLimiteTotal} onChange={(e) => setProdLimiteTotal(e.target.value)}
                placeholder="p.ej. 3" />
              <p className="mt-1 text-[11px] text-slate-500">
                Hoy el límite total es <b>{productosUso?.limite_efectivo ?? 0}</b>
                {" "}({productosUso?.limite_plan ?? 0} incluidos por el plan + {productosUso?.productos_extra ?? 0} extra)
                {" "}· productos activos: {productosUso?.activos ?? 0}. El mínimo es lo incluido por el plan.
              </p>
            </div>
            <div>
              <Label htmlFor="prod-motivo" className="text-sm font-medium">Motivo del ajuste (obligatorio)</Label>
              <Textarea id="prod-motivo" data-testid="prod-motivo-input" rows={3}
                value={prodMotivo} onChange={(e) => setProdMotivo(e.target.value)}
                placeholder="Ej. Transferencia SPEI 15/10/2026 ref. 123456: se autoriza un total de 3 productos." />
              <p className="mt-1 text-[11px] text-slate-500">
                Mínimo 10 caracteres. Queda en la bitácora de la entidad con el límite anterior
                y el nuevo. ({prodMotivo.trim().length})
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProdAjusteOpen(false)} disabled={prodBusy}>Cancelar</Button>
            <Button data-testid="prod-ajuste-confirmar" onClick={submitAjusteProductos}
              disabled={prodBusy || prodLimiteTotal === "" || prodMotivo.trim().length < 10}
              className="bg-accent hover:bg-forest-700">
              {prodBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <SlidersHorizontal className="mr-1.5 h-4 w-4" />}
              Fijar límite total
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de rechazo de solicitud de alta (super_admin) */}
      <Dialog open={rechazoOpen} onOpenChange={(v) => { if (!v) { setRechazoOpen(false); setRechazoMotivo(""); } }}>
        <DialogContent data-testid="entidad-rechazo-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Rechazar solicitud de alta</DialogTitle>
            <DialogDescription>
              El motivo se registra en bitácora y se comunica al representante para que corrija y reenvíe la solicitud.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="entidad-rechazo-motivo" className="text-sm font-medium">Motivo del rechazo *</Label>
            <Textarea id="entidad-rechazo-motivo" data-testid="entidad-rechazo-motivo" rows={4}
              placeholder="Ej. La constancia fiscal no corresponde al RFC declarado."
              value={rechazoMotivo} onChange={(e) => setRechazoMotivo(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRechazoOpen(false); setRechazoMotivo(""); }}>Cancelar</Button>
            <Button data-testid="entidad-rechazo-confirmar"
              disabled={busy || !rechazoMotivo.trim()}
              onClick={doRechazarSolicitud}
              className="bg-red-600 hover:bg-red-700 text-white">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <XCircle className="mr-1.5 h-4 w-4" />}
              Rechazar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
